// =============================================================================
// RunbookPage — renders RUNBOOK.md inline at /admin/runbook
//
// Single source of truth is /RUNBOOK.md at the monorepo root. Vite's
// `?raw` import inlines the file contents at build time via the
// `@runbook` alias configured in vite.config.ts. No runtime fetch, no
// stale copies in src/.
//
// We use react-markdown + remark-gfm to support GitHub-flavored
// markdown (tables, task list checkboxes, fenced code blocks). The
// `prose` classes come from @tailwindcss/typography and give it a
// readable look without manually styling every element.
//
// Access control: this is super-admin only (the runbook contains
// references to operational secrets + deploy steps that aren't useful
// or appropriate for tenant admins). Wrap with the <Admin> helper in
// App.tsx so the SuperAdminRoute guard kicks in.
// =============================================================================

import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, ExternalLink, Copy, Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// rehype-slug auto-generates `id` attributes on every heading using the
// same github-slugger algorithm that GitHub uses to render anchor links
// in README files. That makes the runbook's existing Table-of-Contents
// links like `[Architecture overview](#1-architecture-overview)` resolve
// to actual headings — without this plugin the headings render as plain
// <h2>…</h2> with no id and the anchor links 404 within the page.
import rehypeSlug from 'rehype-slug';
// RUNBOOK.md lives at the monorepo root — single source of truth.
// `?raw` inlines its contents at build time so this page always
// reflects whatever was committed at deploy. Relative path (5 hops
// up from src/pages/super-admin/) instead of a Vite alias because
// alias resolution doesn't reliably compose with `?raw` for files
// outside the project root — Netlify's Rollup pass failed with
// "Cannot resolve import @runbook?raw". Plain relative works.
//
// Requires vite.config.ts to have `server.fs.allow: ['..', '../..']`
// so the dev server can read above the project root. Build doesn't
// need this — Rollup inlines at bundle time.
import runbookMd from '../../../../../RUNBOOK.md?raw';

export function RunbookPage() {
  const [copied, setCopied] = useState(false);

  // Initial-load anchor scroll. When someone shares
  // /admin/runbook#1-architecture-overview, the browser's native
  // scroll-to-hash happens BEFORE the markdown finishes rendering, so
  // the target heading doesn't exist yet → browser silently gives up.
  // Wait one tick after mount, then manually scroll if a hash is
  // present. Smooth behavior matches in-page TOC clicks.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    // requestAnimationFrame gives the markdown one paint cycle to land.
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Convenience copy-link button — operator can drop the URL into a
  // chat/email when handing the runbook to another engineer.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers / unsecured contexts — ignore silently
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/*
        Color note: the surface-* palette in globals.css is ROLE-BASED
        (surface-700 = "body text", surface-100 = "card bg") and the
        CSS variable *values* flip between light and dark mode. So
        `text-surface-700` is dark text in light mode AND light text
        in dark mode — DO NOT add a `dark:text-surface-200` override
        here, because in dark mode surface-200 = #1a2435 (basically
        the page bg) and the text becomes invisible. Only the brand-*
        scale is mode-stable; for brand we do still use a dark:
        variant to brighten the icon in dark mode.
      */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 mb-4"
      >
        <ArrowLeft size={14} />
        Back to admin
      </Link>

      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <BookOpen size={24} className="text-brand-600 dark:text-brand-400" />
          <h1 className="text-2xl font-bold text-surface-900">Environment Standup Runbook</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 border border-overlay/[0.08]"
            title="Copy URL to clipboard"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <a
            href="https://github.com/arrivetechoperations/DiscoveryGem-SAAS-Platform/blob/main/RUNBOOK.md"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 border border-overlay/[0.08]"
            title="View on GitHub (latest main)"
          >
            <ExternalLink size={12} />
            GitHub
          </a>
        </div>
      </div>

      <p className="text-sm text-surface-600 mb-6">
        End-to-end instructions for provisioning a new DiscoveryGem environment (stage or prod).
        Source of truth is{' '}
        <code className="font-mono text-[0.9em] px-1.5 py-0.5 rounded bg-surface-100 text-surface-900 border border-overlay/[0.08]">
          RUNBOOK.md
        </code>{' '}
        at the repo root — any edit to that file ships with the next deploy.
      </p>

      {/*
        The markdown article. Every element gets an explicit color in
        both light + dark mode — `prose-invert` alone wasn't enough
        because the previous `prose-surface` variant doesn't exist
        in @tailwindcss/typography (gray/slate/zinc/neutral/stone are
        the only built-in palettes), so the prose was silently
        falling back to the default gray which had poor contrast
        against the surface-0 page background in both modes.

        Class groups below, in order:
          - headings / scroll offset for anchor links
          - body paragraphs + list items
          - inline code chip (no decorative backticks)
          - fenced code blocks (dark in both modes, subtle border)
          - links (brand color, ~7:1 contrast both modes)
          - strong/bold (high-contrast accent for MUST/DO NOT callouts)
          - GFM tables (explicit cell borders + row hover)
          - block quotes (left border + tinted bg for callouts)
          - horizontal rules (visible separator)

        DO NOT put C-style /* ... *\/ comments inside the className
        string — JSX treats className as a plain string, so those
        characters end up as literal class names (and any embedded
        double quotes terminate the string mid-attribute, causing a
        TS1003 / TS1382 parse error).
      */}
      <article
        className="
          prose dark:prose-invert max-w-none
          prose-headings:scroll-mt-24
          prose-headings:text-surface-900
          prose-p:text-surface-700
          prose-li:text-surface-700
          prose-code:before:content-none prose-code:after:content-none
          prose-code:font-mono prose-code:text-[0.9em] prose-code:font-medium
          prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-code:bg-surface-100 prose-code:text-surface-900
          dark:prose-code:bg-brand-900/50 dark:prose-code:text-brand-50
          prose-code:border prose-code:border-overlay/[0.08]
          dark:prose-code:border-brand-700/40
          prose-pre:bg-surface-900 prose-pre:text-surface-50
          prose-pre:border prose-pre:border-surface-800
          prose-pre:rounded-lg prose-pre:shadow-md
          dark:prose-pre:bg-gradient-to-br
          dark:prose-pre:from-brand-900 dark:prose-pre:via-brand-800 dark:prose-pre:to-brand-900
          dark:prose-pre:text-brand-50
          dark:prose-pre:border-brand-700
          dark:prose-pre:shadow-lg dark:prose-pre:ring-1 dark:prose-pre:ring-brand-400/20
          [&_pre_code]:bg-transparent [&_pre_code]:border-0
          [&_pre_code]:p-0 [&_pre_code]:rounded-none
          [&_pre_code]:text-inherit [&_pre_code]:font-medium
          prose-a:text-brand-700 hover:prose-a:text-brand-800
          dark:prose-a:text-brand-400 dark:hover:prose-a:text-brand-300
          prose-a:font-medium prose-a:no-underline hover:prose-a:underline
          prose-strong:text-surface-900 prose-strong:font-semibold
          prose-table:text-sm
          prose-thead:border-b-2 prose-thead:border-overlay/[0.15]
          prose-th:bg-surface-100 prose-th:text-surface-900
          prose-th:px-3 prose-th:py-2 prose-th:text-left
          prose-td:text-surface-700
          prose-td:px-3 prose-td:py-2
          prose-td:border-t prose-td:border-overlay/[0.08]
          prose-tr:hover:bg-overlay/[0.04]
          prose-blockquote:border-l-4 prose-blockquote:border-brand-500
          prose-blockquote:bg-brand-50 dark:prose-blockquote:bg-brand-900/20
          prose-blockquote:text-surface-700
          prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:my-4
          prose-blockquote:not-italic prose-blockquote:rounded-r-md
          prose-hr:border-overlay/[0.12]
        "
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug]}
        >
          {runbookMd}
        </ReactMarkdown>
      </article>
    </div>
  );
}
